/**
 * B632 (c) — the guest's own pages (`/me`): the pure half. The doors are
 * leg (a)'s — session.magiclink.request (public, uniform), the `/me#<mlk1>`
 * link, session.magiclink.open (one use → a gst1 CUSTOMER session) — and
 * order.list under that session is fenced to the email's own orders (B25c).
 */
import type { StorageLike } from "./session.js";
import { buyerTicketStatus } from "./confirmation.js";
import { formatMoney } from "./money.js";
import { GENERIC_VOCABULARY } from "@transact/ui";

/* ------------------------------------------------------------------ route */

export interface MeRoute {
  /** The link from the email (the fragment, so it never reaches a server log). */
  readonly linkToken?: string;
  /** The storefront the page belongs to, when no link names it (`?tenant=` / `?siteKey=`, the storefront's own query). */
  readonly tenantId?: string;
  readonly siteKey?: string;
  /** B632 (c) region 2 — one order's page (`&order=`), reached by MANAGE. */
  readonly orderId?: string;
  /** B722 — the spec's `/me/orders[/<id>]`: the buyer ASKED for their orders, so a signed-out page says why it asks. */
  readonly asked?: true;
}

export function meRouteFrom(loc: { readonly pathname: string; readonly hash: string; readonly search: string }): MeRoute | undefined {
  /* B722 — `/me/orders` and `/me/orders/<id>` (the spec's paths) are the same page; they fell to the poster wall */
  const asked = /\/me\/orders(?:\/([^/]+))?\/?$/.exec(loc.pathname);
  if (asked === null && !/\/me\/?$/.test(loc.pathname)) return undefined;
  const pathOrder = asked?.[1] !== undefined ? decodeURIComponent(asked[1]) : "";
  const token = loc.hash.replace(/^#/, "").trim();
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(loc.search);
  } catch {
    params = new URLSearchParams();
  }
  const tenantId = params.get("tenant") ?? "";
  const siteKey = params.get("siteKey") ?? "";
  const orderId = pathOrder !== "" ? pathOrder : (params.get("order") ?? "");
  return {
    ...(token !== "" ? { linkToken: token } : {}),
    ...(tenantId !== "" ? { tenantId } : {}),
    ...(siteKey !== "" ? { siteKey } : {}),
    ...(orderId !== "" ? { orderId } : {}),
    ...(asked !== null ? { asked: true as const } : {}),
  };
}

/** B722 — the venue's own facts, read from the guest door (`session.guest`) on every load. */
export interface MeVenueFacts {
  readonly businessName?: string;
  readonly tenantType?: string;
  readonly logoUrl?: string;
  readonly timezone?: string;
  readonly timeFormat?: "12h" | "24h";
}

/** The guest door's answer, narrowed to what /me wears (the token it mints is not kept: /me signs in by its own link). */
export function meVenueFacts(res: unknown): MeVenueFacts {
  const r = (typeof res === "object" && res !== null ? res : {}) as Record<string, unknown>;
  const theme = (typeof r["theme"] === "object" && r["theme"] !== null ? r["theme"] : {}) as Record<string, unknown>;
  const str = (v: unknown): string | undefined => (typeof v === "string" && v.trim() !== "" ? v : undefined);
  const businessName = str(r["businessName"]);
  const tenantType = str(r["tenantType"]);
  const logoUrl = str(theme["logoUrl"]);
  const timezone = str(r["timezone"]);
  const timeFormat = r["timeFormat"] === "12h" || r["timeFormat"] === "24h" ? r["timeFormat"] : undefined;
  return {
    ...(businessName !== undefined ? { businessName } : {}),
    ...(tenantType !== undefined ? { tenantType } : {}),
    ...(logoUrl !== undefined && /^https:\/\//.test(logoUrl) ? { logoUrl } : {}),
    ...(timezone !== undefined ? { timezone } : {}),
    ...(timeFormat !== undefined ? { timeFormat } : {}),
  };
}

/* B722 — the order a signed-out buyer asked for, kept per tenant so the link (opened in another tab) comes back to it. */
const askKey = (tenantId: string): string => `transact.storefront.meask.${tenantId}`;

export function rememberMeAsk(storage: StorageLike | null, tenantId: string, orderId: string): void {
  try {
    storage?.setItem(askKey(tenantId), orderId);
  } catch {
    /* the buyer lands on their orders instead */
  }
}

export function recallMeAsk(storage: StorageLike | null, tenantId: string): string | undefined {
  try {
    const v = storage?.getItem(askKey(tenantId));
    return typeof v === "string" && v.trim() !== "" ? v.trim() : undefined;
  } catch {
    return undefined;
  }
}

export function forgetMeAsk(storage: StorageLike | null, tenantId: string): void {
  try {
    storage?.removeItem(askKey(tenantId));
  } catch {
    /* nothing kept */
  }
}

/** MANAGE → the order's page; the session rides the per-tenant store, so the address carries only names. */
export function meOrderUrl(tenantId: string, orderId: string): string {
  return `/me?tenant=${encodeURIComponent(tenantId)}&order=${encodeURIComponent(orderId)}`; // vocab-ok: a URL query key, never rendered
}

/** B632 — the storefront head's MY TICKETS door: a hosted page's own tenant; an embed (B517) or an unknown tenant draws none. */
export function meDoorHref(distribution: string, tenantId: string): string | undefined {
  return distribution === "embedded" || tenantId === "" ? undefined : meOrdersUrl(tenantId);
}

/** ‹ YOUR ORDERS. */
export function meOrdersUrl(tenantId: string): string {
  return `/me?tenant=${encodeURIComponent(tenantId)}`; // vocab-ok: a URL query key, never rendered
}

/** The tenant and channel a link names — read only to route the open call; the server verifies the signature. */
export function magicLinkScope(token: string): { readonly tenantId: string; readonly channelId: string } | undefined {
  const parts = token.split(".");
  if (parts.length !== 4 || parts[0] !== "mlk1") return undefined;
  try {
    const b64 = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const bytes = Uint8Array.from(atob(b64 + "=".repeat((4 - (b64.length % 4)) % 4)), (c) => c.charCodeAt(0));
    const json = JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
    const tenantId = json["tenantId"];
    const channelId = json["channelId"];
    return typeof tenantId === "string" && tenantId !== "" && typeof channelId === "string" && channelId !== "" ? { tenantId, channelId } : undefined;
  } catch {
    return undefined;
  }
}

/** The email field's own check, in words — before any call. */
export function emailWhy(email: string): string | undefined {
  const e = email.trim();
  if (e === "") return "Enter the email you bought your tickets with.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return "That doesn't look like an email address — check it and try again.";
  return undefined;
}

/* ---------------------------------------------------------------- session */

export interface MeSession {
  readonly token: string;
  readonly customerId: string;
  readonly email: string;
  readonly expiresAt: number;
}

/**
 * The signed-in session, under its OWN key per tenant — never the
 * storefront's guest token, which a cart rides: a buyer who signs in to see
 * their orders and then buys must not have the two sessions trade places.
 */
export function createMeStore(storage: StorageLike | null, tenantId: string, now: () => number = Date.now) {
  const key = `transact.storefront.me.${tenantId}`;
  const clear = (): void => {
    try {
      storage?.removeItem(key);
    } catch {
      /* nothing kept */
    }
  };
  return {
    get(): MeSession | undefined {
      try {
        const raw = storage?.getItem(key);
        if (raw === null || raw === undefined) return undefined;
        const s = JSON.parse(raw) as Partial<MeSession>;
        if (typeof s.token !== "string" || typeof s.email !== "string" || typeof s.customerId !== "string" || typeof s.expiresAt !== "number") return undefined;
        if (now() >= s.expiresAt) {
          clear();
          return undefined;
        }
        return s as MeSession;
      } catch {
        return undefined;
      }
    },
    set(s: MeSession): void {
      try {
        storage?.setItem(key, JSON.stringify(s));
      } catch {
        /* memory carries it for this visit */
      }
    },
    clear,
  };
}

/* ------------------------------------------------------------------ cards */

export interface MeOrderLine {
  readonly lineId: string;
  readonly productId: string;
  readonly productName?: string;
  readonly variantId?: string;
  readonly variantName?: string;
  /** B632 (e) — the slot the line sits on (`<scheduleId>#<local start>`), what a move starts from. */
  readonly slotId?: string;
  readonly slotDate?: string;
  readonly slotTime?: string;
  readonly quantity: number;
  readonly paid: { readonly amount: number; readonly currency: string };
}

/** The slice of an order.list summary the cards read. */
export interface MeOrder {
  readonly orderId: string;
  readonly state: string;
  readonly total: { readonly amount: number; readonly currency: string };
  readonly lines?: readonly MeOrderLine[];
  readonly confirmationCode?: string;
  readonly createdAt?: number;
}

export type MeChipTone = "confirmed" | "due" | "void";

export interface MeCard {
  readonly orderId: string;
  readonly show: string;
  /** "Fri, Oct 9 · 7:00 PM" — the earliest showtime on the order; absent when no line names one. */
  readonly when?: string;
  /** "2 × General Admission · 1 × VIP". */
  readonly what: string;
  readonly code?: string;
  readonly chip: { readonly word: string; readonly tone: MeChipTone };
}

/** Orders a buyer holds — a cart, an unpaid checkout or one that lapsed is not one. */
const HELD = new Set(["CONFIRMED", "PARTIALLY_REFUNDED", "REFUNDED", "CANCELLED"]);
const LIVE = new Set(["CONFIRMED", "PARTIALLY_REFUNDED"]);

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-09" + "19:00" → "Fri, Oct 9 · 7:00 PM" — the slot's own local words; no zone arithmetic. */
export function showtimeWords(date: string, time?: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const day = DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
  const head = `${day}, ${MONTHS[m - 1]!} ${String(d)}`;
  if (time === undefined) return head;
  const [hh, mm] = time.split(":").map(Number) as [number, number];
  return `${head} · ${String(((hh + 11) % 12) + 1)}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
}

const firstSlot = (o: MeOrder): MeOrderLine | undefined =>
  [...(o.lines ?? [])].filter((l) => l.slotDate !== undefined).sort((a, b) => `${a.slotDate!}${a.slotTime ?? ""}`.localeCompare(`${b.slotDate!}${b.slotTime ?? ""}`))[0];

function chipOf(state: string, past: boolean): MeCard["chip"] {
  if (state === "REFUNDED") return { word: "REFUNDED", tone: "due" };
  if (state === "PARTIALLY_REFUNDED") return { word: "PART REFUNDED", tone: "due" };
  if (state === "CANCELLED") return { word: "CANCELLED", tone: "void" };
  /* a past show: the order does not say whether the tickets were scanned, so the chip claims only that it has played */
  return past ? { word: "ENDED", tone: "void" } : { word: "CONFIRMED", tone: "confirmed" };
}

function cardOf(o: MeOrder, past: boolean): MeCard {
  const lines = o.lines ?? [];
  const shows = [...new Set(lines.map((l) => l.productName).filter((n): n is string => n !== undefined))].sort((a, b) => a.localeCompare(b));
  const counts = new Map<string, number>();
  for (const l of lines) {
    const name = l.variantName ?? "Ticket";
    counts.set(name, (counts.get(name) ?? 0) + l.quantity);
  }
  const slot = firstSlot(o);
  return {
    orderId: o.orderId,
    show: shows.length === 0 ? "Your booking" : shows.length === 1 ? shows[0]! : `${shows[0]!} + ${String(shows.length - 1)} more`,
    ...(slot?.slotDate !== undefined ? { when: showtimeWords(slot.slotDate, slot.slotTime) } : {}),
    what: [...counts].map(([name, n]) => `${String(n)} × ${name}`).join(" · "),
    ...(o.confirmationCode !== undefined ? { code: o.confirmationCode } : {}),
    chip: chipOf(o.state, past),
  };
}

/**
 * 649:111 — COMING UP: held orders still live whose first show is today or
 * later, soonest first; PAST: every other held order, newest first.
 * `today` is the buyer's own date ("YYYY-MM-DD"), the slot's date its own.
 */
export function meCards(orders: readonly MeOrder[], today: string): { readonly upcoming: readonly MeCard[]; readonly past: readonly MeCard[] } {
  const held = orders.filter((o) => HELD.has(o.state));
  const dateOf = (o: MeOrder): string => {
    const s = firstSlot(o);
    return s === undefined ? "" : `${s.slotDate!}${s.slotTime ?? ""}`;
  };
  const isUpcoming = (o: MeOrder): boolean => LIVE.has(o.state) && (firstSlot(o)?.slotDate ?? "") >= today;
  const upcoming = held.filter(isUpcoming).sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
  const past = held.filter((o) => !isUpcoming(o)).sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
  return { upcoming: upcoming.map((o) => cardOf(o, false)), past: past.map((o) => cardOf(o, true)) };
}

/** TICKETS → the B606 ticket page, the order's tokens in its fragment. */
export function meTicketsUrl(tokens: readonly string[]): string {
  return `/t#${tokens.join(",")}`;
}

/** "0:42" — the resend clock. */
export function clockWords(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${String(Math.floor(s / 60))}:${String(s % 60).padStart(2, "0")}`;
}

/**
 * "Today" for COMING UP: the date a show's day could still be running
 * ANYWHERE a venue sits — twelve hours behind UTC. A slot's date is its
 * venue's own and no zone rides the order, so a show stays COMING UP until
 * its day has ended even on the Pacific's far side: it errs late, never
 * early, and reads no device clock (B659).
 */
export function meToday(ms: number): string {
  const d = new Date(ms - 12 * 3_600_000);
  return `${String(d.getUTCFullYear())}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

/* ------------------------------------------------------------ order page */

type Money = { readonly amount: number; readonly currency: string };

/** The tenant's self-service rule as `order.get` carries it (B632 (c)): flags + the guest's sentences, in that order. */
export interface MeSelfServiceRule {
  readonly refunds: "NONE" | "FULL" | "WINDOW";
  readonly refundHoursBefore?: number;
  readonly refundKeepBps?: number;
  readonly changes: "NONE" | "SAME_SHOW" | "ANY_SHOW";
  readonly addTickets: boolean;
  readonly resend: boolean;
  /** [refunds, changes, add tickets (when offered), re-send (when offered)] — the domain's one voice. */
  readonly sentences: readonly string[];
}

/** The slice of an `order.get` answer the order page reads. */
export interface MeOrderDetail {
  readonly orderId: string;
  readonly state: string;
  readonly total: Money;
  readonly lines: readonly MeOrderLine[];
  readonly confirmationCode?: string;
  readonly createdAt?: number;
  readonly fees?: readonly { readonly feeId: string; readonly label: string; readonly amount: Money }[];
  readonly taxTotal?: Money;
  readonly promotions?: readonly { readonly promotionId: string; readonly label?: string; readonly code?: string; readonly amount: Money }[];
  readonly acknowledged?: { readonly line: string; readonly at: number };
  readonly refunded?: Money;
  readonly payment?: { readonly tender: string; readonly brand?: string; readonly last4?: string } | undefined;
  readonly selfService?: MeSelfServiceRule;
}

/** One ticket: `entitlement.list`'s state + type, joined to its signed token from `order.tokens` when one came back. */
export interface MeTicket {
  readonly entitlementId: string;
  readonly state: string;
  readonly variantName?: string;
  readonly token?: string;
}

export interface MeTicketFace {
  readonly entitlementId: string;
  readonly name: string;
  readonly ref: string;
  /** The wallet's word (B448's table), upper-cased for the chip; absent for a state the table has no word for. */
  readonly status?: string;
  /** Present only when the ticket can still be scanned — the QR is drawn from it. */
  readonly token?: string;
}

export interface MeCanDoRow {
  readonly key: "change" | "add" | "refund" | "transfer";
  readonly title: string;
  readonly body: string;
  readonly offered: boolean;
}

export interface MeOrderFace {
  readonly orderId: string;
  readonly show: string;
  readonly chip: MeCard["chip"];
  /** "Friday, October 9 · 7:00 PM". */
  readonly when?: string;
  /** "Order 366V-8349 · 2 × General Admission · bought Thu, Oct 1". */
  readonly facts: string;
  readonly tickets: readonly MeTicketFace[];
  /** Absent when the rule is not on the answer, the show has played, or the order is over. */
  readonly canDo?: readonly MeCanDoRow[];
  /** RESEND EMAIL is offered (the tenant's rule; a stage without the rule keeps the door — order.resend is the buyer's own). */
  readonly resend: boolean;
  readonly agreed?: readonly string[];
  readonly paidOn?: string;
  readonly paid: readonly { readonly label: string; readonly amount: string }[];
  readonly total: { readonly label: string; readonly amount: string };
}

const LONG_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const LONG_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "2026-10-09" + "19:00" → "Friday, October 9 · 7:00 PM" (650:2's head) — the slot's own local words. */
export function showtimeLongWords(date: string, time?: string): string {
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  const head = `${LONG_DAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!}, ${LONG_MONTHS[m - 1]!} ${String(d)}`;
  return time === undefined ? head : `${head} · ${showtimeWords(date, time).split(" · ")[1]!}`;
}

/** "Thu, Oct 1" — the moment the order was placed, on the VENUE's calendar (B659: never the device's zone). */
function boughtOn(ms: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric", timeZone }).format(ms);
}

const negative = (m: Money): string => `–${formatMoney({ amount: Math.abs(m.amount), currency: m.currency })}`;
const BRANDS: Readonly<Record<string, string>> = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", discover: "Discover", jcb: "JCB", diners: "Diners", unionpay: "UnionPay" };

/** 650:2's WHAT YOU CAN STILL DO, in the tenant's own sentences (the domain's order: refunds, changes, add, re-send). */
function canDoRows(rule: MeSelfServiceRule, unit: string): readonly MeCanDoRow[] {
  const [refunds, changes] = rule.sentences;
  return [
    { key: "change", title: `Change the ${unit}`, body: changes ?? "", offered: rule.changes !== "NONE" },
    {
      key: "add",
      title: "Add tickets to this order",
      body: rule.addTickets ? (rule.sentences[2] ?? "You can add tickets to your booking.") : "Not offered online — the box office can add tickets for you.",
      offered: rule.addTickets,
    },
    { key: "refund", title: "Refund", body: refunds ?? "", offered: rule.refunds !== "NONE" },
    { key: "transfer", title: "Transfer to someone else", body: "Not offered here — tickets are checked against the buyer's code only.", offered: false },
  ];
}

/**
 * 650:2 / 651:2 — the order page's view-model, from the reads alone. A fact
 * no read carries drops its clause (no doors time, no venue phone, no
 * hours-left count: the order carries no zone to count in).
 */
export function meOrderFace(
  order: MeOrderDetail,
  tickets: readonly MeTicket[],
  /** The tenant's zone (the storefront's recalled clock) — REQUIRED (B659); unknown = the "bought" date drops, never the device's. */
  opts: {
    readonly timeZone: string | undefined;
    readonly today?: string;
    /** The tenant's slot noun (its vocabulary pack's capacity unit: a SHOW's "showtime", a FERRY's "departure"). */
    readonly unit?: string;
  },
): MeOrderFace {
  const card = cardOf(order, false);
  const slot = firstSlot(order);
  const today = opts.today;
  const played = today !== undefined && slot?.slotDate !== undefined && slot.slotDate < today;
  const chip = chipOf(order.state, played);
  const bought = order.createdAt !== undefined && opts.timeZone !== undefined ? boughtOn(order.createdAt, opts.timeZone) : undefined;
  const facts = [order.confirmationCode !== undefined ? `Order ${order.confirmationCode}` : undefined, card.what !== "" ? card.what : undefined, bought !== undefined ? `bought ${bought}` : undefined]
    .filter((s): s is string => s !== undefined)
    .join(" · ");

  const faces = tickets.map((t, i): MeTicketFace => {
    const word = buyerTicketStatus(t.state);
    const live = t.state === "ISSUED" || t.state === "ACTIVE";
    return {
      entitlementId: t.entitlementId,
      name: t.variantName ?? "Ticket",
      ref: `Ticket ${String(i + 1)} of ${String(tickets.length)} · show this at the door`,
      ...(word !== undefined ? { status: word.toUpperCase() } : {}),
      ...(live && t.token !== undefined ? { token: t.token } : {}),
    };
  });

  /* PAID — each ticket type at what it cost; a promotion over one type is shown off that type's pre-promotion price */
  const groups = new Map<string, { quantity: number; amount: number; currency: string }>();
  for (const l of order.lines) {
    const name = l.variantName ?? "Ticket";
    const g = groups.get(name) ?? { quantity: 0, amount: 0, currency: l.paid.currency };
    groups.set(name, { quantity: g.quantity + l.quantity, amount: g.amount + l.paid.amount, currency: g.currency });
  }
  const promos = order.promotions ?? [];
  const promoOff = promos.reduce((sum, p) => sum + p.amount.amount, 0);
  const single = groups.size === 1;
  const paid: { label: string; amount: string }[] = [...groups].map(([name, g]) => ({
    label: `${String(g.quantity)} × ${name}`,
    amount: formatMoney({ amount: single ? g.amount - promoOff : g.amount, currency: g.currency }),
  }));
  for (const p of promos) {
    const name = p.code !== undefined ? `Promo ${p.code}` : (p.label ?? "Promotion");
    paid.push({ label: single ? name : `${name} · in the prices above`, amount: negative(p.amount) });
  }
  for (const f of order.fees ?? []) paid.push({ label: f.label, amount: formatMoney(f.amount) });
  if (order.taxTotal !== undefined) paid.push({ label: "Tax", amount: formatMoney(order.taxTotal) });
  if (order.refunded !== undefined && order.refunded.amount > 0) paid.push({ label: "Refunded", amount: negative(order.refunded) });
  const brand = order.payment?.brand !== undefined ? (BRANDS[order.payment.brand.toLowerCase()] ?? order.payment.brand) : undefined;
  const card4 = order.payment?.last4 !== undefined ? `${brand ?? "Card"} ••${order.payment.last4}` : brand;

  const rule = order.selfService;
  const open = LIVE.has(order.state) && !played;
  const agreed = order.acknowledged?.line
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
  return {
    orderId: order.orderId,
    show: card.show,
    chip,
    ...(slot?.slotDate !== undefined ? { when: showtimeLongWords(slot.slotDate, slot.slotTime) } : {}),
    facts,
    tickets: faces,
    ...(rule !== undefined && open ? { canDo: canDoRows(rule, opts.unit ?? GENERIC_VOCABULARY.capacityUnit.singular) } : {}),
    resend: rule === undefined ? true : rule.resend,
    ...(agreed !== undefined && agreed.length > 0 ? { agreed } : {}),
    ...(bought !== undefined ? { paidOn: bought } : {}),
    paid,
    total: { label: card4 !== undefined ? `Total · ${card4}` : "Total", amount: formatMoney(order.total) },
  };
}

/** Join `entitlement.list` to `order.tokens` by entitlement — a ticket whose token did not come back keeps its row, without a QR. */
export function meTickets(
  entitlements: readonly { readonly entitlementId: string; readonly state: string; readonly variantName?: string }[],
  tokens: readonly { readonly entitlementId: string; readonly token: string }[],
): readonly MeTicket[] {
  const byId = new Map(tokens.map((t) => [t.entitlementId, t.token]));
  return entitlements
    .filter((e) => e.state !== "VOIDED")
    .map((e) => {
      const token = byId.get(e.entitlementId);
      return { entitlementId: e.entitlementId, state: e.state, ...(e.variantName !== undefined ? { variantName: e.variantName } : {}), ...(token !== undefined ? { token } : {}) };
    });
}

